import {
  useCallback,
  useLayoutEffect,
  useRef,
  useState,
  type MouseEvent as ReactMouseEvent,
  type PointerEvent as ReactPointerEvent,
  type RefObject,
} from 'react';

const STORAGE_PREFIX = 'stfors.colw.v1.';
const MIN_COL_WIDTH = 48;

function storageKey(tableId: string): string {
  return `${STORAGE_PREFIX}${tableId}`;
}

function loadAll(tableId: string): Record<string, number> {
  try {
    const raw = localStorage.getItem(storageKey(tableId));
    if (!raw) return {};
    const parsed = JSON.parse(raw) as Record<string, unknown>;
    const out: Record<string, number> = {};
    for (const [k, v] of Object.entries(parsed)) {
      if (typeof v === 'number' && Number.isFinite(v) && v >= MIN_COL_WIDTH) out[k] = Math.round(v);
    }
    return out;
  } catch {
    return {};
  }
}

function saveWidth(tableId: string, column: string, width: number): void {
  try {
    const all = loadAll(tableId);
    all[column] = Math.round(width);
    localStorage.setItem(storageKey(tableId), JSON.stringify(all));
  } catch {
    /* private mode / quota — ignore */
  }
}

function clearWidth(tableId: string, column: string): void {
  try {
    const all = loadAll(tableId);
    delete all[column];
    if (Object.keys(all).length === 0) localStorage.removeItem(storageKey(tableId));
    else localStorage.setItem(storageKey(tableId), JSON.stringify(all));
  } catch {
    /* ignore */
  }
}

function resolveTableId(th: HTMLElement, explicit?: string): string {
  if (explicit) return explicit;
  const host = th.closest('[data-table-id]');
  return host?.getAttribute('data-table-id') || 'default';
}

function applyWidthToTh(th: HTMLElement, widthPx: number | null): void {
  const table = th.closest('table');
  if (widthPx == null) {
    th.style.width = '';
    th.style.minWidth = '';
    th.style.maxWidth = '';
    return;
  }
  const w = `${Math.round(widthPx)}px`;
  th.style.width = w;
  th.style.minWidth = w;
  th.style.maxWidth = w;
  if (table) {
    table.classList.add('has-col-widths');
    table.style.tableLayout = 'fixed';
  }
}

/**
 * Per-column width for data tables. Drag the header edge to resize;
 * double-click the handle to reset. Widths persist in localStorage under
 * `stfors.colw.v1.<tableId>`. Resolve tableId from `data-table-id` on
 * the nearest `.table-scroll` (see `<TableScroll id="…">`).
 */
export function useColWidth(column: string, tableId?: string, enabled = true): {
  /** Matches React's `<th ref>` type (`HTMLTableHeaderCellElement`). */
  thRef: RefObject<HTMLTableHeaderCellElement>;
  width: number | undefined;
  onResizePointerDown: (e: ReactPointerEvent) => void;
  onResizeDoubleClick: (e: ReactMouseEvent) => void;
} {
  const thRef = useRef<HTMLTableHeaderCellElement>(null);
  const [width, setWidth] = useState<number | undefined>(undefined);
  const restored = useRef(false);

  // Restore once the th is mounted so we can read data-table-id from the DOM.
  useLayoutEffect(() => {
    const th = thRef.current;
    // A column that can't be resized also ignores a width saved before it
    // stopped being resizable — otherwise that stale width could never be reset.
    if (!th || restored.current || !enabled) return;
    restored.current = true;
    const id = resolveTableId(th, tableId);
    const stored = loadAll(id)[column];
    if (stored != null) {
      setWidth(stored);
      applyWidthToTh(th, stored);
    }
  }, [column, tableId, enabled]);

  useLayoutEffect(() => {
    const th = thRef.current;
    if (!th) return;
    applyWidthToTh(th, width ?? null);
  }, [width]);

  const onResizePointerDown = useCallback(
    (e: ReactPointerEvent) => {
      e.preventDefault();
      e.stopPropagation();
      const th = thRef.current;
      if (!th) return;

      const id = resolveTableId(th, tableId);
      const startX = e.clientX;
      const startW = th.getBoundingClientRect().width;
      const handle = e.currentTarget as HTMLElement;
      handle.classList.add('is-dragging');
      document.body.classList.add('is-col-resizing');

      const onMove = (ev: PointerEvent) => {
        const next = Math.max(MIN_COL_WIDTH, startW + (ev.clientX - startX));
        setWidth(next);
        applyWidthToTh(th, next);
      };
      const onUp = (ev: PointerEvent) => {
        const next = Math.max(MIN_COL_WIDTH, startW + (ev.clientX - startX));
        setWidth(next);
        applyWidthToTh(th, next);
        saveWidth(id, column, next);
        handle.classList.remove('is-dragging');
        document.body.classList.remove('is-col-resizing');
        window.removeEventListener('pointermove', onMove);
        window.removeEventListener('pointerup', onUp);
      };
      window.addEventListener('pointermove', onMove);
      window.addEventListener('pointerup', onUp);
    },
    [column, tableId],
  );

  const onResizeDoubleClick = useCallback(
    (e: ReactMouseEvent) => {
      e.preventDefault();
      e.stopPropagation();
      const th = thRef.current;
      if (!th) return;
      const id = resolveTableId(th, tableId);
      clearWidth(id, column);
      setWidth(undefined);
      applyWidthToTh(th, null);
    },
    [column, tableId],
  );

  return { thRef, width, onResizePointerDown, onResizeDoubleClick };
}
